import { PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  message,
  notification
} from "antd";
import type { ColumnsType } from "antd/lib/table";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CURRENT_CLUB_ID } from "../../_helpers/constants";
import { PageTitle } from "../../components";
import { usersApi } from "../../_helpers/apiService";

interface DataType {
  key: string;
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  ssn: string;
  role: string;
}

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 14 },
};

const tailLayout = {
  wrapperCol: { offset: 6, span: 18 },
};

const { Option } = Select;

function Referees() {
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [data, setData] = useState<DataType[]>([]);
  const [oldData, setOldData] = useState<DataType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchKey, setSearchKey] = useState("last_name");
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    fetchReferees();
  }, []);

  const fetchReferees = async () => {
    setLoading(true);
    try {
      const response = await usersApi.getAll({
        role: 'referee',
        limit: 1000
      });

      const referees = response.data.items.map((referee: any) => ({
        ...referee,
        key: referee.id.toString(),
      }));

      setData(referees);
      setOldData(referees);
      setLoading(false);
    } catch (error: any) {
      console.error("Fetch referees error:", error);
      message.error("Шүүгчдийн мэдээлэл татахад алдаа гарлаа");
      setLoading(false);
    }
  };

  const deleteReferee = async (referee: DataType) => {
    try {
      await usersApi.delete(referee.id);
      message.success(
        `"${referee.last_name} ${referee.first_name}" шүүгч амжилттай устгалаа.`
      );
      // Refresh data
      fetchReferees();
    } catch (error: any) {
      console.error("Delete referee error:", error);
      message.error("Шүүгч устгахад алдаа гарлаа");
    }
  };

  const onFinish = () => {
    setLoading(true);
    // Filter locally for simplicity
    const filtered = oldData.filter((item: any) => {
      const value = item[searchKey];
      return value && value.toString().toLowerCase().includes(searchValue?.toLowerCase() || '');
    });
    setData(filtered);
    setLoading(false);
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const handleOk = () => {
    setConfirmLoading(true);

    form
      .validateFields()
      .then(async (values) => {
        try {
          const phoneNumber = values.phoneNumber.startsWith('+976')
            ? values.phoneNumber
            : '+976' + values.phoneNumber;

          // Generate email from phone if not provided
          const email = values.email && values.email.trim()
            ? values.email
            : `referee_${phoneNumber.replace(/[^0-9]/g, '')}@archery.mn`;

          await usersApi.create({
            email: email,
            phone_number: phoneNumber,
            password: values.phoneNumber.replace(/[^0-9]/g, ''), // Use phone as password
            first_name: values.firstName,
            last_name: values.lastName,
            ssn: '', // Required but not needed for referees
            birth_date: '1990-01-01', // Default date for referees
            gender: 'male', // Default value
            role: 'referee',
            is_handicapped: false,
            is_old: false
          });

          setVisible(false);
          form.resetFields();
          setConfirmLoading(false);
          message.success(
            `"${values.lastName} ${values.firstName}" шүүгч амжилттай үүслээ.`
          );
          fetchReferees(); // Refresh the list
        } catch (error: any) {
          setConfirmLoading(false);
          console.error("Create referee error:", error);
          const errorMessage = error.response?.data?.detail || "Шүүгч нэмэхэд алдаа гарлаа";
          message.error(errorMessage);
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
        setConfirmLoading(false);
      });
  };

  const clearSearchForm = () => {
    searchForm.resetFields();
    setSearchValue(null);
    setData(oldData);
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "№",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (text, row, index) => <>{index + 1}</>,
    },
    {
      title: "Овог",
      dataIndex: "last_name",
      key: "last_name",
      width: 120,
    },
    {
      title: "Нэр",
      dataIndex: "first_name",
      key: "first_name",
      width: 120,
    },
    {
      title: "Имэйл хаяг",
      dataIndex: "email",
      key: "email",
      width: 200,
    },
    {
      title: "Утасны дугаар",
      dataIndex: "phone_number",
      key: "phone_number",
      width: 130,
      render: (phoneNumber) => {
        // Remove +976 prefix if it exists
        if (phoneNumber && phoneNumber.startsWith('+976')) {
          return phoneNumber.substring(4);
        }
        return phoneNumber;
      },
    },
    {
      title: "Үйлдэл",
      key: "action",
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Link to={`/referees/${record.key}`} key={record.key}>
            Харах
          </Link>
          <Popconfirm
            title="Шүүгч устгах"
            description="Та энэ шүүгчийг устгахдаа итгэлтэй байна уу?"
            onConfirm={() => deleteReferee(record)}
            okText="Тийм"
            cancelText="Үгүй"
          >
            <a style={{ color: 'red' }}>Устгах</a>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageTitle
        title="Шүүгчид"
        description="Шүүгчдийн жагсаалт"
        right={
          <Button
            shape="round"
            onClick={() => setVisible(true)}
            icon={<PlusOutlined />}
            size="large"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            Шүүгч нэмэх
          </Button>
        }
      />
      <div className="page-content-wrapper">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <div className="row">
                    <div className="col-lg-12">
                      <Form
                        layout="inline"
                        style={{ marginBottom: 10 }}
                        form={searchForm}
                        initialValues={{ type: searchKey }}
                      >
                        <Form.Item name="type" label="Талбар">
                          <Select
                            value={searchKey}
                            style={{ width: 300 }}
                            onChange={(val) => setSearchKey(val)}
                            allowClear
                          >
                            <Option value="last_name">Овогоор</Option>
                            <Option value="first_name">Нэрээр</Option>
                            <Option value="email">Имэйл хаягаар</Option>
                            <Option value="phone_number">Утасны дугаараар</Option>
                          </Select>
                        </Form.Item>
                        <Form.Item
                          name="name"
                          label="Хайх утга"
                          rules={[
                            {
                              required: true,
                              message: "Хайх утга оруулна уу!",
                            },
                          ]}
                        >
                          <Input
                            onChange={(e: any) =>
                              setSearchValue(e.target.value)
                            }
                          />
                        </Form.Item>
                        <Form.Item {...tailLayout}>
                          <Button type="primary" onClick={() => onFinish()}>
                            Хайх
                          </Button>
                        </Form.Item>
                        <Form.Item {...tailLayout}>
                          <Button
                            type="dashed"
                            onClick={() => clearSearchForm()}
                          >
                            Арилгах
                          </Button>
                        </Form.Item>
                      </Form>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <Table
                      columns={columns}
                      dataSource={data}
                      loading={loading}
                      scroll={{ x: 'max-content' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="Шүүгч нэмэх"
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Нэмэх"
        cancelText="Болих"
        width={720}
        confirmLoading={confirmLoading}
      >
        <Form {...formItemLayout} form={form}>
          <Form.Item
            label="Имэйл хаяг"
            name="email"
            rules={[{ type: 'email', message: "Имэйл хаягийн формат буруу байна!" }]}
          >
            <Input placeholder="Заавал биш" />
          </Form.Item>
          <Form.Item
            label="Овог"
            name="lastName"
            rules={[{ required: true, message: "Овог оруулна уу!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Нэр"
            name="firstName"
            rules={[{ required: true, message: "Нэр оруулна уу!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Утасны дугаар"
            name="phoneNumber"
            rules={[
              {
                required: true,
                message: "Утасны дугаар оруулна уу!",
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
export default Referees;
